import React, { useCallback, useEffect, useRef, useState } from 'react';
import useShiftsService from '../../services/useShiftService';
import { Shift } from '../../models/ShiftModel';
import showToast from '../../utils/toast';
import ShiftForm from '../../components/Shifts/ShiftForm';
import ButtonCancelForm from '../../components/forms/ButtonCancelForm';
import ButtonSaveForm from '../../components/forms/ButtonSaveForm';
import ConfirmAlert from '../../components/Base/ConfirmAlert';
import ShiftsTable from './ShiftTable';
import { useFormErrors } from '../../hooks/useFormErrors';
import Pagination from '../../components/Base/Pagination';
import TableTextSearch from '../../components/Base/TableTextSearch';
import MaintainerPageShell from '../../components/Base/MaintainerPageShell';
import { Clock, Plus } from 'lucide-react';

const ShiftWorkTab: React.FC = () => {
  const svc = useShiftsService();
  const { list, store, update, remove, fetching } = svc;
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
    hasNext: false,
    hasPrevious: false,
  });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Shift | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [savingLocked, setSavingLocked] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [shiftIdToDelete, setShiftIdToDelete] = useState<number | null>(null);
  const activeRequest = useRef<AbortController | null>(null);
  const { fieldErrors, clearErrors, setErrorsFromResponse } = useFormErrors();
  const [searchText, setSearchText] = useState('');
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback((page: number = 1, search?: string) => {
    activeRequest.current?.abort();
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    const filters: Record<string, unknown> = { page };
    const term = (search !== undefined ? search : searchTextRef.current).trim();
    if (term) filters.search = term;

    list(filters as Record<string, string | number>, {
      onSuccess: (resp: any) => {
        const data = resp?.data ?? {};
        setShifts(data.shifts ?? []);

        if (data.pagination) {
          setPagination({
            page: data.pagination.page || page,
            totalPages: data.pagination.total_pages || 1,
            totalCount: data.pagination.total_count || 0,
            pageSize: data.pagination.page_size || 10,
            hasNext: data.pagination.has_next || false,
            hasPrevious: data.pagination.has_previous || false,
          });
        }
      },
      onError: () => showToast('Error cargando turnos', 'error'),
    }, ctrl.signal);
  }, [list]);

  useEffect(() => {
    load();
    return () => {
      activeRequest.current?.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [load]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchText(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => load(1, value), 400);
    },
    [load]
  );

  const openCreate = () => {
    setEditing({ name: '' });
    setFieldError(null);
    setShowForm(true);
  };

  const openEdit = (s: Shift) => {
    setEditing(s);
    setFieldError(null);
    setShowForm(true);
  };

  const callbackSave = async (): Promise<any> => {
    if (!editing) return;

    setFieldError(null);
    setSavingLocked(true);
    clearErrors();

    try {
      if (editing.id) {
        const r = await update(editing.id, { name: editing.name });
        return r;
      } else {
        const r = await store({ name: editing.name });
        return r;
      }
    } catch (error: any) {
      const nameErr =
        error?.errors?.items?.[0]?.name?.[0] ??
        error?.errors?.items?.['0']?.name?.[0];

      if (nameErr) {
        setErrorsFromResponse({ name: [nameErr] });
      }

      throw error;
    } finally {
      setSavingLocked(false);
    }
  };

  const handleSaveComplete = (ok: boolean, resp?: any) => {
    if (ok) {
      showToast(resp?.message ?? 'Guardado', 'success');
      setShowForm(false);
      setEditing(null);
      clearErrors();
      load(pagination.page, searchTextRef.current.trim() || undefined);
      return;
    }

    setErrorsFromResponse(resp);
    showToast(resp?.message ?? 'Error guardando', 'error');
  };

  const handleClose = () => {
    clearErrors();
    setShowForm(false);
    setFieldError(null);
  };

  const openDeleteConfirmation = (id: number) => {
    setShiftIdToDelete(id);
    setIsAlertOpen(true);
  };

  const handleConfirmDelete = () => {
    if (shiftIdToDelete === null) return;

    remove(shiftIdToDelete, {
      onSuccess: () => {
        showToast('Eliminado', 'success');
        load(pagination.page, searchTextRef.current.trim() || undefined);
      },
      onError: () => showToast('Error eliminando', 'error'),
    });

    setIsAlertOpen(false);
    setShiftIdToDelete(null);
  };

  const totalLabel = fetching.list
    ? 'Cargando...'
    : (
      <span className="maintainer-page__meta-count">
        <span className="maintainer-page__meta-pill">
          {pagination.totalCount || shifts.length}
        </span>
        turnos
      </span>
    );

  return (
    <MaintainerPageShell
      title="Turno laboral"
      icon={<Clock size={22} strokeWidth={2.25} />}
      meta={totalLabel}
      actions={
        <button type="button" className="btn-create" onClick={openCreate}>
          <Plus size={16} strokeWidth={2.25} aria-hidden />
          Nuevo turno
        </button>
      }
    >
      <ShiftsTable
        data={shifts}
        loading={fetching.list}
        onEdit={openEdit}
        onDelete={openDeleteConfirmation}
        totalCount={pagination.totalCount}
        searchTerm={searchText}
        onClearSearch={() => handleSearchChange('')}
        toolbar={
          <TableTextSearch
            value={searchText}
            onChange={handleSearchChange}
            placeholder="Buscar por nombre de turno..."
            aria-label="Buscar turnos por nombre"
          />
        }
        footer={
          pagination.totalPages > 1 ? (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              onPageChange={(page) => {
                load(page, searchTextRef.current.trim() || undefined);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          ) : null
        }
      />

      {showForm && editing && (
        <div className="shifts-modal">
          <div className="shifts-modal-inner">
            <h3>{editing.id ? 'Editar turno' : 'Crear turno'}</h3>
            <ShiftForm value={editing} onChange={setEditing} error={fieldError || undefined} fieldErrors={fieldErrors} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <ButtonCancelForm callbackCancel={handleClose} locked={savingLocked} confirmCancel={false} />
              <ButtonSaveForm callbackSave={callbackSave} locked={savingLocked} onComplete={handleSaveComplete} />
            </div>
          </div>
        </div>
      )}

      <ConfirmAlert
        isOpen={isAlertOpen}
        title="¿Eliminar turno?"
        message="Esta acción es permanente y no se puede deshacer."
        onConfirm={handleConfirmDelete}
        onClose={() => setIsAlertOpen(false)}
      />
    </MaintainerPageShell>
  );
};

export default ShiftWorkTab;
